import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Pause, Loader2, GripVertical } from "lucide-react";
import { getMe, definirTarefaAtual } from "../services/me";

function fmtDur(ms: number) {
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}min`;
}

const POS_KEY = "recdc:barra-play-pos";
const MARGEM = 8; // respiro mínimo até a borda da tela
const LIMIAR_ARRASTO = 4; // px — abaixo disso é clique, não arrasto

type Pos = { x: number; y: number };

function lerPos(): Pos | null {
  try {
    const cru = localStorage.getItem(POS_KEY);
    if (!cru) return null;
    const p = JSON.parse(cru);
    return typeof p?.x === "number" && typeof p?.y === "number" ? p : null;
  } catch {
    return null;
  }
}

/** Mantém a pílula dentro da janela (tela menor, zoom, rotação de celular…). */
function limitar(p: Pos, w: number, h: number): Pos {
  const maxX = Math.max(MARGEM, window.innerWidth - w - MARGEM);
  const maxY = Math.max(MARGEM, window.innerHeight - h - MARGEM);
  return { x: Math.min(Math.max(p.x, MARGEM), maxX), y: Math.min(Math.max(p.y, MARGEM), maxY) };
}

/**
 * Barra FIXA de play/pause no canto da tela (pedido da Rec, jul/2026): enquanto
 * a pessoa está com um play ligado, mostra o NOME da tarefa (pra conferir se é a
 * certa) e um botão de PAUSAR — sem precisar abrir a tarefa. Fica visível em
 * qualquer página do sistema (montada no Layout interno). Clicar no nome abre a
 * tarefa. É dentro do navegador (o app desktop que roda com tudo fechado é outra
 * frente, adiada).
 *
 * Dá pra ARRASTAR pra qualquer canto (jul/2026): a posição é por navegador
 * (localStorage) e fica presa dentro da janela. Padrão = inferior esquerdo, pois
 * o ChatFloating ocupa a direita.
 */
export function BarraPlayFixa() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe, refetchInterval: 60000 });
  const [, setTick] = useState(0);

  const elRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(() => lerPos());
  const [arrastando, setArrastando] = useState(false);
  // Bloqueia o clique de "abrir tarefa" quando o pointerup encerra um arrasto.
  const arrastouRef = useRef(false);
  const dragRef = useRef<{ dx: number; dy: number; x0: number; y0: number } | null>(null);
  // Espelho da posição pra ler fora do render (persistir, re-limitar) sem efeito
  // colateral dentro do updater do setState.
  const posRef = useRef<Pos | null>(pos);
  function aplicarPos(p: Pos) {
    posRef.current = p;
    setPos(p);
  }

  const temPlay = !!me?.tarefaAtual && !!me?.tarefaAtualDesde;
  // Atualiza o "há Xmin" a cada 30s — só enquanto há play (evita re-render à toa).
  useEffect(() => {
    if (!temPlay) return;
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, [temPlay]);

  // Reposiciona se a janela encolher e a pílula ficar fora da vista.
  useEffect(() => {
    if (!temPlay) return;
    function aoRedimensionar() {
      const el = elRef.current;
      const p = posRef.current;
      if (!el || !p) return;
      aplicarPos(limitar(p, el.offsetWidth, el.offsetHeight));
    }
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [temPlay]);

  // Se a posição salva veio de uma tela maior, corrige assim que a pílula existe.
  useLayoutEffect(() => {
    const el = elRef.current;
    const p = posRef.current;
    if (!el || !p) return;
    const ok = limitar(p, el.offsetWidth, el.offsetHeight);
    if (ok.x !== p.x || ok.y !== p.y) aplicarPos(ok);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temPlay]);

  const aoMover = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    const el = elRef.current;
    if (!d || !el) return;
    // Só vira arrasto de verdade depois do limiar — um clique trêmulo não conta.
    if (Math.abs(e.clientX - d.x0) > LIMIAR_ARRASTO || Math.abs(e.clientY - d.y0) > LIMIAR_ARRASTO) {
      arrastouRef.current = true;
      setArrastando(true);
    }
    if (!arrastouRef.current) return;
    aplicarPos(limitar({ x: e.clientX - d.dx, y: e.clientY - d.dy }, el.offsetWidth, el.offsetHeight));
  }, []);

  const aoSoltar = useCallback(() => {
    dragRef.current = null;
    setArrastando(false);
    window.removeEventListener("pointermove", aoMover);
    window.removeEventListener("pointerup", aoSoltar);
    window.removeEventListener("pointercancel", aoSoltar);
    // Só grava se ARRASTOU: senão um clique simples no nome congelaria a pílula
    // em coordenadas absolutas e ela deixaria de acompanhar o canto da tela.
    if (arrastouRef.current && posRef.current) {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
      } catch {
        /* modo privado / storage cheio: só não persiste */
      }
    }
    // O clique dispara logo após o pointerup; libera no próximo tick.
    setTimeout(() => {
      arrastouRef.current = false;
    }, 0);
  }, [aoMover]);

  function aoPegar(e: React.PointerEvent) {
    if (e.button !== 0) return;
    // Pausar nunca arrasta: é o botão que a pessoa aperta com pressa.
    if ((e.target as HTMLElement).closest("[data-sem-arrasto]")) return;
    const el = elRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // dx/dy vêm do rect ATUAL (mesmo ainda encostada no canto pelo CSS), então o
    // primeiro move já cai no lugar certo — sem "pulo" e sem fixar em quem só clicou.
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, x0: e.clientX, y0: e.clientY };
    arrastouRef.current = false;
    window.addEventListener("pointermove", aoMover);
    window.addEventListener("pointerup", aoSoltar);
    window.addEventListener("pointercancel", aoSoltar);
  }

  useEffect(() => () => aoSoltar(), [aoSoltar]);

  const pausarM = useMutation({
    mutationFn: () => definirTarefaAtual(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      qc.invalidateQueries({ queryKey: ["trabalhando-agora"] });
    },
    onError: () => alert("Não foi possível pausar agora. Tente pelo botão de play do card."),
  });

  if (!me?.tarefaAtual || !me?.tarefaAtualDesde) return null;
  const tarefa = me.tarefaAtual;
  const decorridoMs = Math.max(0, Date.now() - new Date(me.tarefaAtualDesde).getTime());

  return (
    <div
      ref={elRef}
      onPointerDown={aoPegar}
      style={pos ? { left: pos.x, top: pos.y, touchAction: "none" } : { touchAction: "none" }}
      className={`fixed z-40 max-w-[calc(100vw-1rem)] print:hidden select-none ${
        pos ? "" : "bottom-4 left-4"
      } ${arrastando ? "cursor-grabbing" : "cursor-grab"}`}
    >
      <div
        className={`flex items-center gap-2 bg-emerald-600 text-white rounded-full pl-2 pr-1.5 py-1.5 ${
          arrastando ? "shadow-2xl ring-2 ring-white/40" : "shadow-lg"
        }`}
      >
        <GripVertical size={13} className="shrink-0 text-white/50" aria-hidden />
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
        </span>
        <button
          onClick={() => {
            if (arrastouRef.current) return;
            navigate(`/tarefas?tarefa=${tarefa.id}`);
          }}
          className="flex flex-col items-start min-w-0 text-left"
          title="Abrir a tarefa (arraste a barra para movê-la)"
        >
          <span className="text-[10px] leading-none text-white/80">Play ligado · há {fmtDur(decorridoMs)}</span>
          <span className="text-xs font-semibold leading-tight truncate max-w-[50vw] sm:max-w-[240px]">
            {tarefa.titulo}
          </span>
        </button>
        <button
          data-sem-arrasto
          onClick={() => pausarM.mutate()}
          disabled={pausarM.isPending}
          className="ml-1 shrink-0 inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1.5 text-xs font-medium disabled:opacity-50 cursor-pointer"
          title="Pausar o play desta tarefa"
        >
          {pausarM.isPending ? <Loader2 size={13} className="animate-spin" /> : <Pause size={13} />}
          Pausar
        </button>
      </div>
    </div>
  );
}
